
import { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Video, Image as ImageIcon, Play, Download, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getKieApiKey } from "@/lib/kie-service";

interface JobResult {
    status: "idle" | "uploading" | "processing" | "completed" | "error";
    taskId?: string;
    resultUrl?: string;
    message?: string;
}

interface VideoDanceTabProps {
    userCredits: number;
}

const VideoDanceTab = ({ userCredits }: VideoDanceTabProps) => {
    const { t } = useLanguage();
    const { user, refreshProfile } = useAuth();
    const { toast } = useToast();

    const [refImage, setRefImage] = useState<File | null>(null);
    const [refVideo, setRefVideo] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [videoPreview, setVideoPreview] = useState<string | null>(null);

    const [prompt, setPrompt] = useState("The cartoon character is dancing.");
    const [orientation, setOrientation] = useState("video");
    const [mode, setMode] = useState("720p");

    const [jobResult, setJobResult] = useState<JobResult>({ status: "idle" });

    const fileInputImage = useRef<HTMLInputElement>(null);
    const fileInputVideo = useRef<HTMLInputElement>(null);

    const getRequiredCredits = () => {
        return mode === "1080p" ? 140 : 80;
    };

    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
            if (videoPreview) URL.revokeObjectURL(videoPreview);
        };
    }, [imagePreview, videoPreview]);

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast({ variant: "destructive", title: "Invalid File", description: "Please upload an image file." });
            return;
        }
        setRefImage(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('video/')) {
            toast({ variant: "destructive", title: "Invalid File", description: "Please upload a video file." });
            return;
        }
        setRefVideo(file);
        setVideoPreview(URL.createObjectURL(file));
    };

    const uploadFile = async (file: File) => {
        if (!user) throw new Error("User not logged in");
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const bucket = 'videodance-temp';
        const filePath = `${user.id}/${fileName}`;

        let targetBucket = bucket;

        let { data, error: uploadError } = await supabase.storage
            .from(targetBucket)
            .upload(filePath, file);

        if (uploadError) {
            console.warn(`Upload to ${targetBucket} failed, trying generation-inputs`, uploadError);
            targetBucket = 'generation-inputs';
            const res = await supabase.storage
                .from(targetBucket)
                .upload(filePath, file);
            if (res.error) throw res.error;
            data = res.data;
        }

        const { data: { publicUrl } } = supabase.storage
            .from(targetBucket)
            .getPublicUrl(filePath);

        return publicUrl;
    };

    const handleGenerate = async () => {
        if (!user) {
            toast({ variant: "destructive", title: t("กรุณาเข้าสู่ระบบ", "Please login"), description: t("ต้องเข้าสู่ระบบก่อนใช้งาน", "Login required") });
            return;
        }
        if (!refImage || !refVideo) {
            toast({ variant: "destructive", title: t("ไฟล์ไม่ครบ", "Missing Files"), description: t("กรุณาอัพโหลดทั้งภาพและวิดีโอ", "Please upload both image and video") });
            return;
        }

        const creditsRequired = getRequiredCredits();
        if (userCredits < creditsRequired) {
            toast({ variant: "destructive", title: t("เครดิตไม่เพียงพอ", "Insufficient Credits"), description: t(`ต้องใช้ ${creditsRequired} เครดิต`, `Requires ${creditsRequired} credits`) });
            return;
        }

        setJobResult({ status: "uploading", message: t("กำลังอัพโหลดไฟล์...", "Uploading files...") });

        try {
            // 1. Deduct Credits & Create Log
            let logId: string | undefined;

            const { data: deductResult, error: deductError } = await supabase.rpc("deduct_credits_for_generation" as any, {
                p_user_id: user.id,
                p_credits_required: creditsRequired,
                p_service_type: "video_motion",
                p_model: `kling-motion-control (${mode})`,
                p_prompt: prompt || "Motion Sync"
            });

            if (deductError) throw new Error("Failed to deduct credits: " + deductError.message);

            const result = deductResult as any;
            if (!result.success) {
                throw new Error(result.error || "Insufficient credits or error deducting credits");
            }

            logId = result.log_id;

            // 2. Upload Files
            const imageUrl = await uploadFile(refImage);
            const videoUrl = await uploadFile(refVideo);

            setJobResult({ status: "processing", message: t("กำลังส่งงานไปที่ AI...", "Submitting task to AI...") });

            // 3. Call API
            const apiKey = await getKieApiKey();
            const baseUrl = "https://api.kie.ai";

            const payload = {
                model: "kling-2.6/motion-control",
                input: {
                    prompt: prompt || "The cartoon character is dancing.",
                    input_urls: [imageUrl],
                    video_urls: [videoUrl],
                    character_orientation: orientation,
                    mode: mode
                }
            };

            const response = await fetch(`${baseUrl}/api/v1/jobs/createTask`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${apiKey}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.code !== 200) {
                // Parse error message from API
                const errorMsg = data.msg || data.message || data.error || "Unknown API error";
                const errorDetail = data.data?.message || data.data?.error || "";
                const fullErrorMsg = errorDetail ? `${errorMsg}: ${errorDetail}` : errorMsg;

                console.error("API Error:", { code: data.code, msg: errorMsg, detail: errorDetail, fullResponse: data });

                // If API fails, mark log as failed and refund credits
                if (logId) {
                    // First update status to failed with error message
                    await supabase.from("generation_logs").update({
                        status: "failed",
                        error_message: fullErrorMsg
                    }).eq("id", logId);

                    // Refund credits
                    const { error: refundError } = await supabase.rpc('refund_credits_for_failed_generation' as any, {
                        p_log_id: logId
                    });

                    if (refundError) {
                        console.error("Failed to refund credits:", refundError);
                    } else {
                        await refreshProfile(); // Update credits display
                    }
                }

                // Show error toast with the actual error message
                toast({
                    variant: "destructive",
                    title: t("❌ เกิดข้อผิดพลาด", "❌ Error"),
                    description: fullErrorMsg + (logId ? t("\n\n💰 เครดิตถูกคืนแล้ว", "\n\n💰 Credits have been refunded") : "")
                });

                throw new Error(fullErrorMsg);
            }

            const taskId = data.data.taskId;

            // Update log with taskId and set status to processing
            // Store taskId in both task_id AND result_url for compatibility with task-checker
            if (logId) {
                await supabase.from("generation_logs").update({
                    task_id: taskId,
                    result_url: taskId,  // task-checker uses result_url to find taskId
                    status: "processing"
                }).eq("id", logId);
            }

            // 4. Log to DB (optional update log with taskId if your deduct_credits function returns logId, 
            // but here we just start polling. Ideally we'd link taskId to the transaction log, but the current RPC might not return it easily without modification.
            // We will just proceed.)

            setJobResult({ status: "processing", taskId, message: t("รอคิวประมวลผล (ประมาณ 5-10 นาที)...", "Processing (approx 5-10 mins)...") });
            pollStatus(taskId, apiKey, baseUrl, logId);

        } catch (error: any) {
            console.error("Generation error:", error);
            const errorMessage = error.message || t("เกิดข้อผิดพลาด กรุณาลองใหม่", "Something went wrong. Please try again.");
            setJobResult({ status: "error", message: errorMessage });
        }
    };

    const pollStatus = async (taskId: string, apiKey: string, baseUrl: string, logId?: string) => {
        let attempts = 0;
        const maxAttempts = 120; // 20 mins

        const check = async () => {
            if (attempts >= maxAttempts) {
                const timeoutMsg = t("หมดเวลารอผลลัพธ์ กรุณาลองใหม่อีกครั้ง", "Timeout waiting for result. Please try again.");
                setJobResult(prev => ({ ...prev, status: "error", message: timeoutMsg }));
                if (logId) {
                    await supabase.from("generation_logs").update({ status: "failed", error_message: "Timeout" }).eq("id", logId);
                    // Refund credits on timeout
                    await supabase.rpc('refund_credits_for_failed_generation' as any, { p_log_id: logId });
                    await refreshProfile(); // Update credits display
                    toast({
                        variant: "destructive",
                        title: t("⏱️ หมดเวลา", "⏱️ Timeout"),
                        description: timeoutMsg + t("\n\n💰 เครดิตถูกคืนแล้ว", "\n\n💰 Credits have been refunded")
                    });
                }
                return;
            }

            try {
                const res = await fetch(`${baseUrl}/api/v1/jobs/recordInfo?taskId=${taskId}`, {
                    headers: { "Authorization": `Bearer ${apiKey}` }
                });
                const data = await res.json();

                if (data.code === 200 && data.data) {
                    const { state, resultJson, failMsg } = data.data;

                    if (state === "success" && resultJson) {
                        const result = JSON.parse(resultJson);
                        const videoUrl = result.resultUrls?.[0];

                        if (videoUrl) {
                            setJobResult({ status: "completed", taskId, resultUrl: videoUrl });
                            toast({ title: t("เสร็จสิ้น!", "Success!"), description: t("วิดีโอสร้างเสร็จแล้ว", "Video generated successfully.") });

                            // Update log success
                            if (logId) {
                                await supabase.from("generation_logs").update({
                                    status: "completed",
                                    result_url: videoUrl,
                                    completed_at: new Date().toISOString()
                                }).eq("id", logId);
                            }
                        } else {
                            throw new Error("No URL in result");
                        }
                    } else if (state === "fail") {
                        const errorMessage = failMsg || "Task failed";
                        setJobResult({ status: "error", taskId, message: errorMessage });

                        // Update log fail and refund credits
                        if (logId) {
                            await supabase.from("generation_logs").update({
                                status: "failed",
                                error_message: errorMessage
                            }).eq("id", logId);

                            // Refund credits
                            const { error: refundError } = await supabase.rpc('refund_credits_for_failed_generation' as any, {
                                p_log_id: logId
                            });

                            if (!refundError) {
                                await refreshProfile(); // Update credits display
                                toast({
                                    variant: "destructive",
                                    title: t("❌ การสร้างล้มเหลว", "❌ Generation Failed"),
                                    description: errorMessage + t("\n\n💰 เครดิตถูกคืนแล้ว", "\n\n💰 Credits have been refunded")
                                });
                            }
                        }
                    } else {
                        attempts++;
                        setTimeout(check, 10000);
                    }
                } else {
                    attempts++;
                    setTimeout(check, 10000);
                }
            } catch (e) {
                console.error("Poll error:", e);
                attempts++;
                setTimeout(check, 10000);
            }
        };

        check();
    };

    const handleDownload = async (url: string) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `videodance_${Date.now()}.mp4`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
            console.error("Download failed:", e);
            window.open(url, '_blank');
        }
    };

    return (
        <div className="space-y-6">

            {/* Introduction / Info */}
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                <h3 className="font-semibold flex items-center gap-2 text-primary">
                    <Video className="h-5 w-5" /> Motion Control (Video Dance)
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                    {t("สร้างวิดีโอเต้นจากภาพและวิดีโอต้นแบบ รองรับการขยับตามท่าทาง", "Create dance videos from reference image and video.")}
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">

                {/* Step 1: Image Upload */}
                <div className="space-y-2">
                    <Label>{t("1. รูปภาพต้นแบบ (Character)", "1. Character Image")}</Label>
                    <div
                        className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors text-center relative h-48 flex items-center justify-center overflow-hidden bg-black/5 dark:bg-white/5"
                        onClick={() => fileInputImage.current?.click()}
                    >
                        {imagePreview ? (
                            <>
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white font-medium">
                                    {t("คลิกเพื่อเปลี่ยน", "Click to Change")}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <ImageIcon className="h-8 w-8" />
                                <span>Click to upload image</span>
                            </div>
                        )}
                    </div>
                    <Input ref={fileInputImage} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                </div>

                {/* Step 2: Video Upload */}
                <div className="space-y-2">
                    <Label>{t("2. วิดีโอท่าเต้น (Motion)", "2. Motion Video")}</Label>
                    <div
                        className="border-2 border-dashed rounded-lg p-4 hover:bg-muted/50 cursor-pointer transition-colors text-center relative h-48 flex items-center justify-center overflow-hidden bg-black/5 dark:bg-white/5"
                        onClick={() => fileInputVideo.current?.click()}
                    >
                        {videoPreview ? (
                            <>
                                <video src={videoPreview} className="w-full h-full object-contain" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white font-medium">
                                    {t("คลิกเพื่อเปลี่ยน", "Click to Change")}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <Video className="h-8 w-8" />
                                <span>Click to upload video</span>
                            </div>
                        )}
                    </div>
                    <Input ref={fileInputVideo} type="file" accept="video/*" className="hidden" onChange={handleVideoSelect} />
                </div>
            </div>

            {/* Step 3: Settings */}
            <div className="space-y-4 pt-4 border-t border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Prompt (Optional)</Label>
                        <Input
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="The cartoon character is dancing."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Orientation</Label>
                            <Select value={orientation} onValueChange={setOrientation}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="video">Video (Follow Video)</SelectItem>
                                    <SelectItem value="image">Image (Follow Image)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Quality / Credits</Label>
                            <Select value={mode} onValueChange={setMode}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="720p">720p (80 Credits)</SelectItem>
                                    <SelectItem value="1080p">1080p (140 Credits)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Generate Button */}
            <Button
                variant="hero"
                size="lg"
                className="w-full gap-2 text-lg"
                onClick={handleGenerate}
                disabled={jobResult.status === 'uploading' || jobResult.status === 'processing'}
            >
                {jobResult.status === 'uploading' || jobResult.status === 'processing' ? (
                    <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {jobResult.message || "Processing..."}
                    </>
                ) : (
                    <>
                        <Play className="h-5 w-5 fill-current" />
                        {t(`สร้าง ({credits} เครดิต)`, `Generate ({credits} Credits)`).replace('{credits}', getRequiredCredits().toString())}
                    </>
                )}
            </Button>

            {/* Result Display Inline */}
            {jobResult.status === 'completed' && jobResult.resultUrl && (
                <div className="mt-8 p-4 bg-muted/30 rounded-xl border border-border animate-in fade-in slide-in-from-bottom-4">
                    <h4 className="font-semibold mb-4 text-center">{t("ผลลัพธ์วิดีโอ", "Generated Video Result")}</h4>
                    <video
                        src={jobResult.resultUrl}
                        controls
                        autoPlay
                        loop
                        className="max-w-full h-auto max-h-[600px] mx-auto rounded-lg shadow-lg mb-4 bg-black"
                    />
                    <div className="flex justify-center">
                        <Button onClick={() => handleDownload(jobResult.resultUrl!)} variant="default" className="gap-2">
                            <Download className="h-4 w-4" /> Download Video
                        </Button>
                    </div>
                </div>
            )}

            {jobResult.status === 'error' && (
                <div className="mt-8 p-4 bg-red-500/10 rounded-xl border border-red-500/20 text-red-500">
                    <p className="font-bold text-center mb-2">{t("❌ เกิดข้อผิดพลาด", "❌ Error")}</p>
                    <p className="text-sm text-center mb-3">{jobResult.message}</p>
                    <p className="text-xs text-center text-muted-foreground">
                        {t("💰 หากเครดิตถูกหักไป ระบบจะคืนให้อัตโนมัติ", "💰 If credits were deducted, they will be refunded automatically.")}
                    </p>
                    <div className="flex justify-center mt-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setJobResult({ status: "idle" })}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                            {t("ลองใหม่อีกครั้ง", "Try Again")}
                        </Button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default VideoDanceTab;
